import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import clsx from "clsx";
import type { DayView, POI, Tag, ZonedTimeString } from "@poi-planner/shared";
import { useTripStore } from "../../store/tripStore";
import { useLibraryStore } from "../../store/libraryStore";
import { useUIStore } from "../../store/uiStore";
import { blockStartTime, computeDayViews } from "../../utils/dayViews";
import {
  addMinutes,
  epochMs,
  minutesBetween,
  withTimeOfDay,
  zonedDate,
  zonedTimeOfDay,
} from "../../utils/zonedTime";
import { MIN_SPAN_MINUTES } from "../../utils/timeGrid";
import { checkConstraintViolations } from "../../utils/constraints";
import { formatShortDay, formatTimeBudget } from "../../utils/format";
import { Button } from "../Button";
import { TagIcon } from "../TagIcon";
import { buildAccommodationContext } from "./accommodationContext";
import { AccommodationBanner } from "./AccommodationBanner";
import { useNow } from "../../hooks/useNow";
import {
  findTodayView,
  isAllDayNotePast,
  isTimedItemPast,
  nowTimeLabel,
} from "../../utils/nowTime";
import { NOW_MARKER_ATTRIBUTE } from "./NowButton";
import type { DayAccommodationContext } from "./types";

function formatAgendaDate(date: string): string {
  return new Date(date + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}

/** Short label for a zone badge, e.g. "Asia/Tokyo" → "TOKYO" */
function zoneBadgeLabel(zone: string): string {
  const city = zone.split("/").pop() ?? zone;
  return city.replace(/_/g, " ").toUpperCase();
}

interface TimeCommit {
  startTime: ZonedTimeString;
  endTime: ZonedTimeString;
}

/**
 * Inline-editable time range for an agenda row. Click swaps the readout for
 * start/end time inputs (wall clock in the block's own zone, date preserved);
 * Enter or focus-out commits, Escape cancels. Locked rows surface the locked
 * prompt instead of editing.
 */
function AgendaTimeRange({
  startTime,
  endTime,
  locked,
  isReadOnly,
  darkTone,
  onCommit,
}: {
  startTime: ZonedTimeString;
  endTime: ZonedTimeString;
  locked: boolean;
  isReadOnly: boolean;
  darkTone: boolean;
  onCommit: (times: TimeCommit) => void;
}) {
  const showLockedActionPrompt = useUIStore((s) => s.showLockedActionPrompt);
  const [editing, setEditing] = useState<{ start: string; end: string } | null>(
    null,
  );

  const beginEdit = () => {
    if (isReadOnly) return;
    if (locked) {
      showLockedActionPrompt("Unlock this item before changing its time.");
      return;
    }
    setEditing({
      start: zonedTimeOfDay(startTime),
      end: zonedTimeOfDay(endTime),
    });
  };

  const commit = () => {
    if (!editing) return;
    setEditing(null);
    if (!editing.start || !editing.end) return;

    const newStart = withTimeOfDay(startTime, editing.start);
    let newEnd = withTimeOfDay(endTime, editing.end);
    if (epochMs(newEnd) <= epochMs(newStart)) {
      newEnd = addMinutes(newStart, MIN_SPAN_MINUTES);
    }
    if (newStart === startTime && newEnd === endTime) return;
    onCommit({ startTime: newStart, endTime: newEnd });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") commit();
    if (e.key === "Escape") setEditing(null);
  };

  if (editing) {
    return (
      <div
        className="flex flex-col gap-1"
        onBlur={(e) => {
          // Commit once focus leaves both inputs
          if (!e.currentTarget.contains(e.relatedTarget as Node)) commit();
        }}
      >
        <input
          type="time"
          className="input-base w-24 px-1.5 py-0.5 text-xs tabular-nums"
          value={editing.start}
          onChange={(e) =>
            setEditing((prev) => prev && { ...prev, start: e.target.value })
          }
          onKeyDown={handleKeyDown}
          aria-label="Start time"
          autoFocus
        />
        <input
          type="time"
          className="input-base w-24 px-1.5 py-0.5 text-xs tabular-nums"
          value={editing.end}
          onChange={(e) =>
            setEditing((prev) => prev && { ...prev, end: e.target.value })
          }
          onKeyDown={handleKeyDown}
          aria-label="End time"
        />
      </div>
    );
  }

  const start = zonedTimeOfDay(startTime);
  const end = zonedTimeOfDay(endTime);
  return (
    <Button
      variant="unstyled"
      onClick={beginEdit}
      disabled={isReadOnly}
      aria-label={
        isReadOnly ? `${start} – ${end}` : `Edit times ${start} – ${end}`
      }
      className={clsx(
        "flex min-w-14 flex-col rounded-md px-1.5 py-1 text-left tabular-nums leading-tight transition-colors",
        !isReadOnly &&
          (darkTone
            ? "cursor-pointer hover:bg-slate-700"
            : "cursor-pointer hover:bg-subtle"),
      )}
      title={isReadOnly ? undefined : "Edit times"}
    >
      <span
        className={clsx(
          "text-sm font-bold",
          darkTone ? "text-slate-50" : "text-primary",
        )}
      >
        {start}
      </span>
      <span
        className={clsx(
          "text-xs font-semibold",
          darkTone ? "text-slate-400" : "text-tertiary",
        )}
      >
        {end}
      </span>
    </Button>
  );
}

type AgendaRowTone = "poi" | "note" | "allDay";

const agendaRowToneClasses: Record<AgendaRowTone, string> = {
  poi: "border border-border bg-surface hover:bg-sky-50",
  note: "border-l-4 border-sky-500 bg-slate-800 text-slate-200 hover:bg-slate-700",
  allDay:
    "border border-calendar-all-day-border bg-calendar-all-day-bg text-calendar-all-day-text",
};

/**
 * Card shell for an agenda row: a leading column (times or the all-day badge)
 * beside a content button that opens the item's editor. The two are siblings
 * so the inline time editor never nests inside the open action.
 */
function AgendaRow({
  tone,
  lead,
  label,
  onOpen,
  isPast = false,
  children,
}: {
  tone: AgendaRowTone;
  lead: ReactNode;
  label: string;
  onOpen: () => void;
  /** The item has already ended: drawn dimmed. */
  isPast?: boolean;
  children: ReactNode;
}) {
  return (
    <li
      className={clsx(
        "flex items-start gap-1 rounded-xl shadow-sm transition-colors",
        agendaRowToneClasses[tone],
        isPast && "calendar-past",
      )}
    >
      <div className="shrink-0 py-1.5 pl-1.5">{lead}</div>
      <Button
        variant="unstyled"
        onClick={onOpen}
        aria-label={label}
        className="flex min-w-0 flex-1 cursor-pointer items-start gap-2 self-stretch rounded-r-xl py-2.5 pr-3 pl-1 text-left focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-info"
      >
        {children}
      </Button>
    </li>
  );
}

/** The red current-time divider within today's agenda section. */
function AgendaNowLine({
  label,
  as: Tag,
}: {
  label: string;
  as: "li" | "div";
}) {
  return (
    <Tag
      className="flex items-center"
      data-testid="agenda-now-line"
      {...{ [NOW_MARKER_ATTRIBUTE]: "" }}
    >
      <span className="rounded bg-calendar-now px-1.5 py-px text-2xs font-extrabold text-white tabular-nums">
        <span className="sr-only">Now, </span>
        {label}
      </span>
      <span className="size-2 -mr-1 rounded-full bg-calendar-now" aria-hidden />
      <span className="h-0.5 flex-1 bg-calendar-now" aria-hidden />
    </Tag>
  );
}

/** Splices the now-line into a day's rows before the first future start. */
function withNowLine(
  rows: ReactNode[],
  index: number | null,
  label: string,
): ReactNode[] {
  if (index === null) return rows;
  return [
    ...rows.slice(0, index),
    <AgendaNowLine key="now" as="li" label={label} />,
    ...rows.slice(index),
  ];
}

/** Multi-day span readout, e.g. "Wed 14 Oct → Thu 15 Oct". */
function AgendaDateSpan({
  startDate,
  endDate,
  className,
}: {
  startDate: string;
  endDate: string;
  className?: string;
}) {
  return (
    <span className={clsx("whitespace-nowrap font-bold", className)}>
      {formatShortDay(startDate)} → {formatShortDay(endDate)}
    </span>
  );
}

/**
 * Chronological agenda: every scheduled block (POI visits + notes) grouped by
 * trip date and sorted by start instant, with inline time editing. Empty days
 * collapse to compact headers.
 */
export function CalendarAgendaView({
  dayViews: selectedDayViews,
}: {
  dayViews?: DayView[];
} = {}) {
  const trip = useTripStore((s) => s.trip);
  const schedule = useTripStore((s) => s.schedule);
  const isReadOnly = useTripStore((s) => s.isReadOnly);
  const updateScheduledPoi = useTripStore((s) => s.updateScheduledPoi);
  const updateNote = useTripStore((s) => s.updateNote);
  const pois = useLibraryStore((s) => s.pois);
  const tags = useLibraryStore((s) => s.tags);
  const getAccommodation = useLibraryStore((s) => s.getAccommodation);
  const openScheduledVisitEditor = useUIStore(
    (s) => s.openScheduledVisitEditor,
  );
  const openNoteEditor = useUIStore((s) => s.openNoteEditor);

  const allDayViews = useMemo(
    () => (trip ? computeDayViews(trip, schedule) : []),
    [trip, schedule],
  );
  const dayViews = selectedDayViews ?? allDayViews;
  const now = useNow();
  const todayView = useMemo(
    () => findTodayView(dayViews, now),
    [dayViews, now],
  );
  const nowLabel = todayView ? nowTimeLabel(now, todayView.zone) : "";
  const scrollRef = useRef<HTMLDivElement>(null);

  // Open on the now-line while the trip is under way
  useEffect(() => {
    const scroller = scrollRef.current;
    const marker = scroller?.querySelector<HTMLElement>(
      `[${NOW_MARKER_ATTRIBUTE}]`,
    );
    if (!scroller || !marker) return;
    scroller.scrollTop = Math.max(
      marker.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top +
        scroller.scrollTop -
        scroller.clientHeight / 3,
      0,
    );
  }, [trip?.id]);

  const poiMap = useMemo(
    () => new Map<string, POI>(pois.map((poi) => [poi.id, poi])),
    [pois],
  );
  const tagMap = useMemo(
    () => new Map<string, Tag>(tags.map((tag) => [tag.id, tag])),
    [tags],
  );
  const accommodationByDate = useMemo(() => {
    const contexts = new Map<string, DayAccommodationContext>();
    for (const dayView of dayViews) {
      contexts.set(
        dayView.date,
        buildAccommodationContext(dayView.date, schedule, getAccommodation),
      );
    }
    return contexts;
  }, [dayViews, schedule, getAccommodation]);

  if (!trip) return null;

  const readOnly = isReadOnly();

  return (
    <div
      ref={scrollRef}
      className="custom-scrollbar flex-1 overflow-y-auto bg-page"
      data-testid="calendar-agenda-view"
    >
      <div className="mx-auto max-w-2xl px-3 pb-4 md:px-6 md:pb-5">
        {dayViews.map((dayView, index) => {
          const agendaBlocks = dayView.blocks.filter(
            (block) => block.segment.isStart,
          );
          const allDayBlocks = dayView.allDayBlocks.filter(
            (block) => block.segment.isStart,
          );
          const accommodation = accommodationByDate.get(dayView.date)!;
          const hasAccommodation = !!accommodation.sleepPoi;
          const hasBlocks = agendaBlocks.length > 0 || allDayBlocks.length > 0;
          const isEmpty = !hasBlocks && !hasAccommodation;
          const isToday = dayView.date === todayView?.date;
          const nowIndex = isToday
            ? agendaBlocks.filter((block) =>
                isTimedItemPast(blockStartTime(block), now),
              ).length
            : null;
          return (
            <section key={dayView.date} className={isEmpty ? "mb-1" : "mb-3"}>
              {/* Date header — sticks while its day scrolls; compact when the
                  day has no blocks */}
              <div
                className={clsx(
                  "sticky top-0 z-10 flex flex-wrap items-baseline gap-x-2 bg-page",
                  isEmpty ? "py-1 opacity-60" : "pt-3 pb-2",
                )}
              >
                <span className="text-sm font-extrabold tracking-tight">
                  {formatAgendaDate(dayView.date)}
                </span>
                <span className="text-xs font-semibold text-secondary">
                  Day {index + 1}
                </span>
                {dayView.zone !== trip.defaultTimezone && (
                  <span
                    className="rounded bg-purple-100 px-1 py-px text-2xs font-extrabold tracking-wider text-purple-700"
                    title={`This day runs on ${dayView.zone} (trip default: ${trip.defaultTimezone})`}
                  >
                    {zoneBadgeLabel(dayView.zone)}
                  </span>
                )}
              </div>

              {isToday && !hasBlocks && (
                <div className="mb-2">
                  <AgendaNowLine as="div" label={nowLabel} />
                </div>
              )}

              {!isEmpty && hasBlocks && (
                <ul className="flex flex-col gap-2">
                  {allDayBlocks.map(({ note }) => (
                    <AgendaRow
                      key={note.id}
                      tone="allDay"
                      isPast={isAllDayNotePast(
                        note.allDayEndDate,
                        dayView.zone,
                        now,
                      )}
                      label={`${readOnly ? "View" : "Edit"} all-day note ${note.name}`}
                      onOpen={() => openNoteEditor("edit", note.id)}
                      lead={
                        <span className="flex min-w-14 flex-col items-start px-1.5 py-1 text-2xs font-extrabold uppercase leading-tight tracking-wider">
                          <span
                            className="material-symbols-outlined text-base"
                            aria-hidden
                          >
                            wb_sunny
                          </span>
                          All day
                        </span>
                      }
                    >
                      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className="line-clamp-2 break-words text-sm font-bold leading-snug">
                          {note.name}
                        </span>
                        {note.allDayEndDate !== note.allDayStartDate &&
                          note.allDayStartDate &&
                          note.allDayEndDate && (
                            <AgendaDateSpan
                              startDate={note.allDayStartDate}
                              endDate={note.allDayEndDate}
                              className="text-xs opacity-75"
                            />
                          )}
                      </span>
                      {note.locked && (
                        <span
                          className="material-symbols-outlined text-base leading-none"
                          title="Dates locked"
                        >
                          lock
                        </span>
                      )}
                    </AgendaRow>
                  ))}
                  {withNowLine(
                    agendaBlocks.map((block) => {
                      if (block.type === "poi") {
                        const sp = block.scheduledPOI;
                        const poi = poiMap.get(sp.poiId);
                        if (!poi) return null;
                        const primaryTag = poi.primaryTagId
                          ? tagMap.get(poi.primaryTagId)
                          : undefined;
                        const violations = checkConstraintViolations(
                          poi,
                          dayView.date,
                          zonedTimeOfDay(sp.startTime),
                        );
                        const span = Math.max(
                          Math.round(minutesBetween(sp.startTime, sp.endTime)),
                          0,
                        );

                        return (
                          <AgendaRow
                            key={block.id}
                            tone="poi"
                            isPast={isTimedItemPast(sp.endTime, now)}
                            label={`${readOnly ? "View" : "Edit"} scheduled visit to ${poi.name}`}
                            onOpen={() => openScheduledVisitEditor(sp.id)}
                            lead={
                              <AgendaTimeRange
                                startTime={sp.startTime}
                                endTime={sp.endTime}
                                locked={sp.locked}
                                isReadOnly={readOnly}
                                darkTone={false}
                                onCommit={(times) =>
                                  updateScheduledPoi(sp.id, times)
                                }
                              />
                            }
                          >
                            {primaryTag && (
                              <span className="mt-0.5 shrink-0">
                                <TagIcon icon={primaryTag.icon} size={16} />
                              </span>
                            )}
                            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                              <span className="line-clamp-2 break-words text-sm font-bold leading-snug tracking-tight text-primary">
                                {poi.name}
                              </span>
                              <span className="flex flex-wrap items-center gap-x-2 text-xs font-semibold text-tertiary">
                                <span className="whitespace-nowrap">
                                  {formatTimeBudget(span)}
                                </span>
                                {block.segment.count > 1 && (
                                  <AgendaDateSpan
                                    startDate={zonedDate(sp.startTime)}
                                    endDate={zonedDate(sp.endTime)}
                                    className="text-info"
                                  />
                                )}
                              </span>
                              {violations.length > 0 && (
                                <span className="flex items-start gap-1 text-xs font-semibold text-warning">
                                  <span
                                    className="material-symbols-outlined text-sm leading-none"
                                    aria-hidden
                                  >
                                    warning
                                  </span>
                                  {violations
                                    .map((violation) => violation.message)
                                    .join(" · ")}
                                </span>
                              )}
                            </span>
                            {sp.locked && (
                              <span
                                className="material-symbols-outlined text-base leading-none text-tertiary"
                                title="Time locked"
                              >
                                lock
                              </span>
                            )}
                          </AgendaRow>
                        );
                      }

                      const note = block.note;
                      const noteTags = note.tagIds
                        .map((tagId) => tagMap.get(tagId))
                        .filter((tag): tag is Tag => !!tag);
                      const startPoi = note.startPoiId
                        ? poiMap.get(note.startPoiId)
                        : undefined;
                      const endPoi = note.endPoiId
                        ? poiMap.get(note.endPoiId)
                        : undefined;
                      const routeSubtitle =
                        startPoi || endPoi
                          ? `${startPoi?.name ?? ""} → ${endPoi?.name ?? ""}`.trim()
                          : null;
                      const span = Math.max(
                        Math.round(
                          minutesBetween(note.startTime, note.endTime),
                        ),
                        0,
                      );

                      return (
                        <AgendaRow
                          key={block.id}
                          tone="note"
                          isPast={isTimedItemPast(note.endTime, now)}
                          label={`${readOnly ? "View" : "Edit"} note ${note.name}`}
                          onOpen={() => openNoteEditor("edit", note.id)}
                          lead={
                            <AgendaTimeRange
                              startTime={note.startTime}
                              endTime={note.endTime}
                              locked={note.locked}
                              isReadOnly={readOnly}
                              darkTone
                              onCommit={(times) => updateNote(note.id, times)}
                            />
                          }
                        >
                          {noteTags[0] && (
                            <span className="mt-0.5 shrink-0">
                              <TagIcon
                                icon={noteTags[0].icon}
                                size={16}
                                whiteTint
                              />
                            </span>
                          )}
                          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <span className="line-clamp-2 break-words text-sm font-bold leading-snug tracking-tight text-slate-50">
                              {note.name}
                            </span>
                            {routeSubtitle && (
                              <span className="break-words text-xs font-semibold text-slate-300">
                                {routeSubtitle}
                              </span>
                            )}
                            <span className="flex flex-wrap items-center gap-x-2 text-xs font-semibold text-slate-400">
                              <span className="whitespace-nowrap">
                                {formatTimeBudget(span)}
                              </span>
                              {block.segment.count > 1 && (
                                <AgendaDateSpan
                                  startDate={zonedDate(note.startTime)}
                                  endDate={zonedDate(note.endTime)}
                                  className="text-sky-300"
                                />
                              )}
                            </span>
                          </span>
                          {note.locked && (
                            <span
                              className="material-symbols-outlined text-base leading-none text-slate-400"
                              title="Time locked"
                            >
                              lock
                            </span>
                          )}
                        </AgendaRow>
                      );
                    }),
                    nowIndex,
                    nowLabel,
                  )}
                </ul>
              )}

              {!isEmpty && hasAccommodation && (
                <div className={hasBlocks ? "mt-2" : undefined}>
                  <AccommodationBanner
                    date={dayView.date}
                    accommodation={accommodation}
                    isReadOnly={readOnly}
                  />
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
